import type { ComparisonRow } from './comparisonData';
import { spotlightGradientStyle } from '../primitives/SpotlightBox';
import { Check } from '../icons';
import { useSpotlight } from '../../hooks/useSpotlight';
import { ACCENT, BORDER, FONT_MONO, SURFACE, TEXT_PRIMARY, TEXT_SECONDARY } from '../../tokens';

interface RivalBlockProps {
  label: string;
  title: string;
  desc: string;
}

/** One of the two competitor columns — deliberately muted against Augment OS. */
function RivalBlock({ label, title, desc }: RivalBlockProps) {
  return (
    <div
      style={{
        padding: 14,
        background: 'rgba(255,255,255,0.02)',
        borderRadius: 10,
        border: `1px solid ${BORDER}`,
      }}
    >
      <div style={{ fontFamily: FONT_MONO, fontSize: '0.6rem', color: '#555', letterSpacing: '0.08em', marginBottom: 6 }}>
        {label}
      </div>
      <div style={{ fontSize: '1rem', fontWeight: 500, color: '#888', marginBottom: 6 }}>{title}</div>
      <div style={{ fontSize: '0.75rem', color: '#666', lineHeight: 1.5 }}>{desc}</div>
    </div>
  );
}

interface ComparisonCardProps {
  item: ComparisonRow;
}

/**
 * One category compared three ways.
 *
 * Unlike the other spotlit cards this one does not lift on hover — it sits in
 * a horizontal snap-scroller, where a transform would fight the scroll.
 */
export function ComparisonCard({ item }: ComparisonCardProps) {
  const { containerRef, spotRef, hovered, handlers } = useSpotlight<HTMLDivElement>();

  return (
    <div
      ref={containerRef}
      {...handlers}
      style={{
        flex: '0 0 min(300px, 85vw)',
        scrollSnapAlign: 'start',
        background: SURFACE,
        border: `1px solid ${hovered ? 'rgba(255,107,0,0.35)' : BORDER}`,
        borderRadius: 20,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        transition: 'border-color 0.3s',
        position: 'relative',
      }}
    >
      <div ref={spotRef} aria-hidden="true" style={spotlightGradientStyle(hovered)} />

      <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>
        <div style={{ padding: 24, borderBottom: `1px solid ${BORDER}`, background: 'rgba(255,255,255,0.02)' }}>
          <div style={{ fontSize: '1.5rem', marginBottom: 12, color: ACCENT }}>{item.icon}</div>
          <div
            style={{
              fontFamily: FONT_MONO,
              fontSize: '0.7rem',
              color: ACCENT,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
            }}
          >
            {item.category}
          </div>
        </div>

        <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 16, flex: 1 }}>
          <RivalBlock label="FULL-SERVICE AGENCY" title={item.agency.title} desc={item.agency.desc} />
          <RivalBlock label="IN-HOUSE OR DIY" title={item.consultants.title} desc={item.consultants.desc} />

          <div
            style={{
              padding: 14,
              background: 'rgba(255,107,0,0.08)',
              borderRadius: 10,
              border: '1px solid rgba(255,107,0,0.3)',
            }}
          >
            <div
              style={{
                fontFamily: FONT_MONO,
                fontSize: '0.6rem',
                color: ACCENT,
                letterSpacing: '0.08em',
                marginBottom: 6,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
              }}
            >
              AUGMENT OS <Check size={10} strokeWidth={3} />
            </div>
            <div style={{ fontSize: '1rem', fontWeight: 600, color: TEXT_PRIMARY, marginBottom: 6 }}>
              {item.augment.title}
            </div>
            <div style={{ fontSize: '0.75rem', color: TEXT_SECONDARY, lineHeight: 1.5 }}>{item.augment.desc}</div>
          </div>
        </div>
      </div>
    </div>
  );
}
